import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { adminApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { formatDate, getStatusColor, getRiskColor } from '../../lib/utils';
import {
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Database,
  Loader2,
  ArrowRight
} from 'lucide-react';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [repoStats, setRepoStats] = useState(null);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsRes, repoRes, submissionsRes, pendingRes] = await Promise.all([
          adminApi.getAnalytics(),
          adminApi.getRepositoryStats(),
          adminApi.listSubmissions({ limit: 5 }),
          adminApi.getPendingCount().catch(() => ({ data: { pending_count: 0 } }))
        ]);
        setAnalytics(analyticsRes.data);
        setRepoStats(repoRes.data);
        setRecentSubmissions(submissionsRes.data);
        setPendingCount(pendingRes.data.pending_count);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const statCards = [
    { 
      label: 'Total Users', 
      value: analytics?.total_users || 0, 
      subValue: `${analytics?.active_users || 0} active`,
      icon: Users, 
      color: 'bg-blue-100 text-blue-700' 
    },
    { 
      label: 'Total Submissions', 
      value: analytics?.total_submissions || 0, 
      subValue: `${analytics?.submissions_last_7_days || 0} this week`,
      icon: FileText, 
      color: 'bg-emerald-100 text-emerald-700' 
    },
    { 
      label: 'Avg Similarity', 
      value: `${analytics?.average_similarity_score || 0}%`, 
      subValue: 'across all scans',
      icon: TrendingUp, 
      color: 'bg-purple-100 text-purple-700' 
    },
    { 
      label: 'Repository Size', 
      value: `${repoStats?.total_size_mb || 0} MB`, 
      subValue: `${repoStats?.total_documents || 0} documents`,
      icon: Database, 
      color: 'bg-amber-100 text-amber-700' 
    }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            System overview and analytics for Riverbank Academy Plagiarism Checker
          </p>
        </div>

        {/* Pending Approvals Alert */}
        {pendingCount > 0 && (
          <Link to="/admin/users">
            <Card className="border-amber-200 bg-amber-50 card-hover cursor-pointer" data-testid="pending-approval-card">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900">{pendingCount} new registration{pendingCount !== 1 ? 's' : ''} pending approval</p>
                    <p className="text-sm text-amber-700">Click to review and approve new users</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-amber-600" />
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="font-mono text-3xl font-bold text-foreground mt-1">
                        {stat.value}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.subValue}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${stat.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Status Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analytics?.status_distribution || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(status)} variant="secondary">
                        {status}
                      </Badge>
                    </div>
                    <span className="font-mono font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-serif">Risk Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(analytics?.risk_distribution || {}).map(([risk, count]) => (
                  <div key={risk} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={getRiskColor(risk)} variant="secondary">
                        {risk} risk
                      </Badge>
                    </div>
                    <span className="font-mono font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Submissions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between border-b border-border/40">
            <CardTitle className="font-serif">Recent Submissions</CardTitle>
            <Link to="/admin/submissions">
              <Button variant="ghost" size="sm" data-testid="view-all-btn">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No submissions yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {recentSubmissions.map((submission) => (
                  <div
                    key={submission.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                    data-testid={`admin-submission-${submission.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{submission.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {submission.file_name} • {formatDate(submission.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {submission.status === 'completed' && (
                        <div className="text-right">
                          <p className="font-mono font-semibold">
                            {submission.similarity_score?.toFixed(1)}%
                          </p>
                          <Badge className={getRiskColor(submission.risk_level)} variant="secondary">
                            {submission.risk_level}
                          </Badge>
                        </div>
                      )}
                      <Badge className={getStatusColor(submission.status)} variant="secondary">
                        {submission.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/users">
            <Card className="card-hover cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold">Manage Users</h3>
                  <p className="text-sm text-muted-foreground">View and manage student accounts</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/submissions">
            <Card className="card-hover cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <FileText className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-semibold">All Submissions</h3>
                  <p className="text-sm text-muted-foreground">Review all document submissions</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/settings">
            <Card className="card-hover cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-sm flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h3 className="font-semibold">System Settings</h3>
                  <p className="text-sm text-muted-foreground">Configure thresholds and rules</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
